import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase/config";

class AuthService {
  constructor() {
    this.currentUser = null;
    this.userProfile = null;
  }

  // Register new user
  async register(email, password, userData) {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Update display name
      await updateProfile(user, {
        displayName: userData.name,
      });

      // Create user profile in Firestore
      await setDoc(doc(db, "users", user.uid), {
        name: userData.name,
        email: userData.email,
        phone: userData.phone || "",
        role: userData.role || "admin", // Default to admin for now
        moneyAmount: userData.role === "admin" ? 0 : null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      return { user, success: true };
    } catch (error) {
      console.error("Registration error:", error);
      return { error: error.message, success: false };
    }
  }

  // Login user
  async login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      return { user: userCredential.user, success: true };
    } catch (error) {
      console.error("Login error:", error);
      return { error: error.message, success: false };
    }
  }

  // Logout user
  async logout() {
    try {
      await signOut(auth);
      this.currentUser = null;
      this.userProfile = null;
      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      return { error: error.message, success: false };
    }
  }

  // Reset password
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error("Password reset error:", error);
      return { error: error.message, success: false };
    }
  }

  // Get user profile from Firestore
  async getUserProfile(uid) {
    try {
      const docRef = doc(db, "users", uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { profile: docSnap.data(), success: true };
      } else {
        return { error: "User profile not found", success: false };
      }
    } catch (error) {
      console.error("Get user profile error:", error);
      return { error: error.message, success: false };
    }
  }

  // Update user profile
  async updateUserProfile(uid, profileData) {
    try {
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        await updateDoc(userRef, {
          ...profileData,
          updatedAt: new Date().toISOString(),
        });

        return { success: true };
      } else {
        return { error: "User not found", success: false };
      }
    } catch (error) {
      console.error("Update user profile error:", error);
      return { error: error.message, success: false };
    }
  }

  // Update user money amount (for admins)
  async updateUserMoney(uid, amount, operation = "add") {
    try {
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        let newAmount = userData.moneyAmount || 0;

        if (operation === "add") {
          newAmount += amount;
        } else if (operation === "subtract") {
          newAmount -= amount;
        } else if (operation === "set") {
          newAmount = amount;
        }

        await updateDoc(userRef, {
          moneyAmount: newAmount,
          updatedAt: new Date().toISOString(),
        });

        return { success: true, newAmount };
      } else {
        return { error: "User not found", success: false };
      }
    } catch (error) {
      console.error("Update user money error:", error);
      return { error: error.message, success: false };
    }
  }

  // Auth state observer
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, async (user) => {
      this.currentUser = user;
      if (user) {
        const profileResult = await this.getUserProfile(user.uid);
        this.userProfile = profileResult.success ? profileResult.profile : null;
      } else {
        this.userProfile = null;
      }
      callback(user, this.userProfile);
    });
  }

  // Get current user
  getCurrentUser() {
    return this.currentUser;
  }

  // Get current user profile
  getCurrentUserProfile() {
    return this.userProfile;
  }
}

export default new AuthService();
